import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import { Alert, Stack } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import CustomButton from 'components/button/CustomButton';
import ConditionList from 'components/condition-list/ConditionList';
import { ListPanelItem } from 'components/condition-list/ListPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import { Role } from 'stores/AuthenticationStoreAdapter';
import { UserResult, deleteUser, getUser } from 'stores/UserStoreAdapter';
import { generateUUID } from 'utils/Common';
import { errorDefault, successDefault } from 'utils/Toastify';

import UserCreateForm, { UserCreateFormHandle } from './components/UserCreateForm';
import UserListItem from './components/UserListItem';
import UserUpdateForm, { UserUpdateFormHandle } from './components/UserUpdateForm';

/** ロール絞り込みの condition キー。API パラメータではなくフロント側フィルタで使う */
const ROLE_FILTER_KEY = 'role';

/** ロール絞り込みの選択肢（表示順を固定） */
const ROLE_FILTER_ORDER: Role[] = ['ADMIN', 'MANAGER', 'BACK_OFFICE', 'GENERAL', 'DEVELOPER'];

const UserManagementPage: React.FC = () => {
  const { userSession } = useAuth();
  const [searchParams] = useSearchParams();
  const [isProgress, setProgress] = useState(true);
  const [users, setUsers] = useState<UserResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // ロールによるフロント側の絞り込み（API パラメータに無いため取得後に絞る）
  const [roleFilter, setRoleFilter] = useState<Role | null>(null);

  const createFormRef = useRef<UserCreateFormHandle>(null);
  const updateFormRef = useRef<UserUpdateFormHandle>(null);

  // クエリパラメータ ?role=XXX を初期条件に反映する（管理ホームのロール別ユーザー数からの遷移用）。
  // 不正・未知のロール値は無視する。初回マウント時のみ評価する。
  const initialRole = React.useMemo<Role | null>(() => {
    const raw = searchParams.get(ROLE_FILTER_KEY);
    return raw && ROLE_FILTER_ORDER.includes(raw as Role) ? (raw as Role) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SearchFilter に渡す初期フィルタ（クエリの role をチップとして復元する）
  const initialFilters = React.useMemo<Filter[]>(() => {
    if (!initialRole) return [];
    const cond = conditions.find((c) => c.key === ROLE_FILTER_KEY)!;
    return [
      {
        id: generateUUID(),
        key: ROLE_FILTER_KEY,
        color: cond.color,
        value: { key: initialRole, name: `${cond.name}: ${Role[initialRole]}` },
      },
    ];
  }, [initialRole]);

  useEffect(() => {
    // 初期ロールがあれば絞り込み state にも反映してから取得する
    setRoleFilter(initialRole);
    fetchUsers({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = (params: Record<string, string>) => {
    setProgress(true);
    getUser(params)
      .then((result: UserResult[]) => {
        setUsers(result);
        setProgress(false);
      })
      .catch((e: Error) => {
        setProgress(false);
        errorDefault('ユーザー情報の取得に失敗しました');
      });
  };

  // ロールでフロント側フィルタした表示用ユーザー一覧
  const visibleUsers = React.useMemo(
    () => (roleFilter ? users.filter((u) => u.roles.includes(roleFilter)) : users),
    [users, roleFilter],
  );

  const handleUpdate = () => {
    fetchUsers({});
    setSelectedUser(null);
    setIsCreateMode(false);
  };

  const handleUserUpdated = (updatedUser: UserResult) => {
    setUsers((prev) => prev.map((u) => (u.userId === updatedUser.userId ? updatedUser : u)));
    setSelectedUser(updatedUser);
  };

  const handleUserDeleted = () => {
    setUsers((prev) => prev.filter((u) => u.userId !== selectedUser?.userId));
    setSelectedUser(null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedUser) return;
    setIsDeleteDialogOpen(false);
    setProgress(true);
    deleteUser({ userId: selectedUser.userId })
      .then(() => {
        successDefault('ユーザーを削除しました');
        handleUserDeleted();
      })
      .catch((e: Error) => {
        errorDefault('ユーザーの削除に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  const handleSearch = (filters: Filter[]) => {
    setSelectedUser(null);
    setIsCreateMode(false);
    // ロールは API 検索に無いためフロント側フィルタへ、それ以外は API パラメータへ振り分ける
    const params: Record<string, string> = {};
    let nextRole: Role | null = null;
    filters.forEach((f) => {
      if (f.key === ROLE_FILTER_KEY) {
        nextRole = f.value.key as Role;
        return;
      }
      params[f.key] = f.value.key;
    });
    setRoleFilter(nextRole);
    fetchUsers(params);
  };

  const handleCreateClick = () => {
    setSelectedUser(null);
    setIsCreateMode(true);
  };

  const handleSelectItem = (item: ListPanelItem) => {
    setIsCreateMode(false);
    setIsEditingUser(false);
    const user = visibleUsers.find((u) => u.userId === item.id);
    if (user) setSelectedUser(user);
  };

  const listItems: ListPanelItem[] = React.useMemo(
    () =>
      visibleUsers.map((u) => ({
        id: u.userId,
        primary: u.userName,
        secondary: u.mailAddress,
      })),
    [visibleUsers],
  );

  const renderUserItem = (item: ListPanelItem) => {
    const user = visibleUsers.find((u) => u.userId === item.id);
    if (!user) return null;
    return <UserListItem user={user} />;
  };

  const isSelf = selectedUser?.userId === userSession.userId;

  const renderFooter = () => {
    if (isCreateMode) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="作成"
            buttonVariant="primary"
            icon={<PersonAddIcon />}
            onClick={() => createFormRef.current?.submit()}
          />
        </Stack>
      );
    }
    if (selectedUser && !isSelf && isEditingUser) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="更新"
            buttonVariant="primary"
            icon={<SaveIcon />}
            onClick={() => updateFormRef.current?.submit()}
          />
        </Stack>
      );
    }
    if (selectedUser && !isSelf && !isEditingUser) {
      return (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <CustomButton
            label="削除"
            buttonVariant="danger"
            icon={<DeleteIcon />}
            onClick={() => setIsDeleteDialogOpen(true)}
          />
          <CustomButton
            label="編集"
            buttonVariant="primary"
            icon={<EditIcon />}
            onClick={() => setIsEditingUser(true)}
          />
        </Stack>
      );
    }
    return undefined;
  };

  return (
    <>
      <DomainBreadcrumbs
        items={[{ label: '管理者メニュー', to: '/management' }, { label: 'ユーザー管理' }]}
      />
      <ConditionList
        conditions={conditions}
        items={listItems}
        selectedId={selectedUser?.userId}
        onSelect={handleSelectItem}
        onSearch={handleSearch}
        onCreateClick={handleCreateClick}
        createButtonLabel="ユーザー新規作成"
        showCreateButton={true}
        renderItem={renderUserItem}
        isProgress={isProgress}
        collapsible={true}
        detailFooter={renderFooter()}
        conditionKey="user-list"
        userProfileKey="management-user-list-search"
        initialFilters={initialFilters}
      >
        {isCreateMode ? (
          <UserCreateForm
            ref={createFormRef}
            onCreate={handleUpdate}
            onCancel={() => setIsCreateMode(false)}
            setProgress={setProgress}
          />
        ) : selectedUser ? (
          <UserUpdateForm
            ref={updateFormRef}
            user={selectedUser}
            isSelf={isSelf}
            editing={isEditingUser}
            onUserUpdated={handleUserUpdated}
            onClose={() => setSelectedUser(null)}
            onEditingChange={setIsEditingUser}
            setProgress={setProgress}
          />
        ) : (
          <></>
        )}
      </ConditionList>

      <DeleteConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="ユーザー削除"
        alertConfirmText={selectedUser?.userId}
      >
        <Alert severity="warning">
          この操作は取り消せません。削除されたユーザーは復元できません。
        </Alert>
      </DeleteConfirmDialog>
    </>
  );
};

const conditions: Condition[] = [
  {
    key: 'userId',
    name: 'ユーザーID',
    type: 'text',
    color: '#78C1F3',
  },
  {
    key: 'userName',
    name: 'ユーザー名',
    type: 'text',
    color: '#3A98B9',
  },
  {
    key: 'mailAddress',
    name: 'メールアドレス',
    type: 'text',
    color: '#B799FF',
  },
  {
    // ロールは API 検索パラメータに無いため、取得後にフロント側で絞り込む（handleSearch 参照）
    key: ROLE_FILTER_KEY,
    name: 'ロール',
    type: 'select',
    color: '#F7A4A4',
    values: ROLE_FILTER_ORDER.map((r) => ({ key: r, name: Role[r] })),
  },
];

export default UserManagementPage;
